import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { GENERAL_ID } from '@components/Stores/exposureDataStore';

import historicalExposure from '../data/HistoricalExposure.json';
import { ToggleChips } from '../../Dashboard/dashboard-components/ToggleChips';
import { IExposureHistory } from '../../../exposureHistory';
import { IMaterialToAccount } from '../../../sp-types/accountTypes';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { FormatToShortPriceTemplate } from '../../../utils/FormatValue';

ChartJS.register(CategoryScale, TimeScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

type HistoricalExposurePoint = {
  date: string;
  hedge: number;
  priceMt: number;
};

type RangeKey = '1Y' | '2Y';

const RANGE_OPTIONS: { label: string; value: RangeKey }[] = [
  { label: '1 Year', value: '1Y' },
  { label: '2 Years', value: '2Y' },
];

const HEDGE_COLOR = '#2563EB';

const parsedExposureData: HistoricalExposurePoint[] = (historicalExposure as HistoricalExposurePoint[]).map(
  (point) => ({
    date: point.date,
    hedge: point.hedge,
    priceMt: point.priceMt,
  }),
);

const sortedExposureData = parsedExposureData
  .slice()
  .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
interface Props {
  exposureData?: IExposureHistory[];
  accountMaterial?: IMaterialToAccount;
  materialId: string;
}
export const ExposureChart: React.FC<Props> = ({ exposureData, accountMaterial, materialId }) => {
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);
  const activeRange = useMemo(() => RANGE_OPTIONS[selectedRangeIndex]?.value ?? '1Y', [selectedRangeIndex]);
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const filteredData = useMemo(
    () => (activeRange === '1Y' ? sortedExposureData.slice(-12) : sortedExposureData),
    [activeRange],
  );
  const ranges = useMemo(() => {
    const divide = materialId === GENERAL_ID ? 1 : pricesMap[materialId] || 1;
    return accountMaterial
      ? [
          accountMaterial.RiskTolerance.dangerAreaMinus.start / divide,
          accountMaterial.RiskTolerance.dangerAreaMinus.end / divide,
          accountMaterial.RiskTolerance.stableArea.start / divide,
          accountMaterial.RiskTolerance.dangerAreaPlus.start / divide,
          accountMaterial.RiskTolerance.dangerAreaPlus.end / divide,
        ]
      : [0, 0, 0, 0, 0];
  }, [materialId, accountMaterial, pricesMap]);
  const THRESHOLD_LINES = [
    {
      key: 'criticalUp',
      label: 'Critical Zone',
      value: ranges[0],
      color: '#EF4444',
      skipLegend: false,
    },
    {
      key: 'cautionUp',
      label: 'Warning Zone',
      value: ranges[1],
      color: '#FACC15',
      skipLegend: false,
    },
    {
      key: 'safeZone',
      label: 'Safe Zone',
      value: ranges[2],
      color: '#22C55E',
      skipLegend: false,
    },
    {
      key: 'caution',
      label: 'Warning Zone ',
      value: ranges[3],
      color: '#FACC15',
      skipLegend: true,
    },
    {
      key: 'critical',
      label: 'Critical Zone',
      value: ranges[4],
      color: '#EF4444',
      skipLegend: true,
    },
  ] as const;

  const dates = filteredData.map((point) => new Date(point.date));
  const lastPoint = filteredData[filteredData.length - 1];

  const chartData = useMemo(() => {
    // Get the full date range from filtered data for threshold lines
    const firstDate = filteredData.length > 0 ? new Date(filteredData[0].date) : new Date();
    const lastFDate = filteredData.length > 0 ? new Date(filteredData[filteredData.length - 1].date) : new Date();
    const lastExpDate =
      exposureData && exposureData.length > 0
        ? new Date(exposureData[exposureData.length - 1].createDate.toDate())
        : new Date();
    const lastDate = lastFDate > lastExpDate ? lastFDate : lastExpDate;

    const thresholdDatasets = THRESHOLD_LINES.map(({ skipLegend, color, value, label }, index) => ({
      label,
      data: [
        { x: firstDate, y: value },
        { x: lastDate, y: value },
      ],
      borderColor: color,
      borderWidth: 1.5,
      borderDash: [2, 6],
      pointRadius: 3,
      pointBackgroundColor: color,
      pointBorderColor: color,
      fill: false,
      skipLegend,
      tension: 0,
      order: index + 1,
    }));

    const calcExposure = ({ futureOrders, inventory, financialPosition }: IExposureHistory) =>
      futureOrders - inventory - financialPosition;

    const exposureDataset = {
      label: 'Exposure',
      data:
        exposureData?.map((exposure) => ({
          x: exposure.createDate.toDate(),
          y: calcExposure(exposure) * pricesMap[exposure.materialId],
        })) || [],
      borderColor: HEDGE_COLOR,
      backgroundColor: HEDGE_COLOR,
      borderWidth: 2.5,
      pointBackgroundColor: '#FFFFFF',
      pointBorderColor: HEDGE_COLOR,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.35,
      fill: false,
      order: 4,
    };
    return {
      labels: dates,
      datasets: [...thresholdDatasets, exposureDataset],
    };
  }, [dates, filteredData, lastPoint, exposureData, THRESHOLD_LINES, pricesMap, materialId]);

  const chartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          type: 'time',
          time: { unit: 'month', tooltipFormat: 'MMM yyyy', displayFormats: { month: 'MMM yy' } },
          grid: { color: '#F1F5F9' },
          ticks: { color: '#475569', maxRotation: 0, autoSkipPadding: 16 },
        },
        y: {
          ticks: {
            color: '#475569',
            callback: (value) => FormatToShortPriceTemplate(+value, materialId === GENERAL_ID ? '$ ' : 'mt ', 0),
          },
          title: {
            display: true,
            text: 'Metric tons',
            color: '#0F172A',
            font: { size: 12, weight: '600' },
          },
          grid: { color: '#E2E8F0' },
        },
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
          labels: {
            usePointStyle: true,
            pointStyle: 'line',
            pointStyleWidth: 24,
            filter: (item, data) => {
              const ds: any = data?.datasets[item?.datasetIndex || 0];
              return !ds?.skipLegend;
            },
          },
        },
        tooltip: {
          intersect: false,
          mode: 'index',
          filter: ({ dataset }: TooltipItem<'line'>) =>
            ['Hedge', 'Price mt', 'Exposure'].includes(dataset.label || '-'),
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              return `${label}: ${value >= 0 ? '+' : ''}${value} mt`;
            },
          },
        },
      },
      elements: {
        line: {
          tension: 0.3,
        },
        point: {
          radius: 4,
          hoverRadius: 6,
        },
      },
    }),
    [materialId],
  );

  const materialLabel = useMemo(
    () => (materialId === GENERAL_ID ? 'General' : materialsMap[materialId]?.name || 'Selected material'),

    [materialId, materialsMap],
  );

  const rangeLabel = useMemo(() => {
    if (!filteredData.length) {
      return '';
    }
    const firstYear = new Date(filteredData[0].date).getFullYear();
    const lastYear = new Date(filteredData[filteredData.length - 1].date).getFullYear();
    return firstYear === lastYear ? `${firstYear}` : `${firstYear} - ${lastYear}`;
  }, [filteredData]);

  return (
    <div className="flex min-h-[320px] flex-1 flex-col bg-white p-6 shadow-md">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{materialLabel} - Historical exposure</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <span className="rounded-full bg-slate-100 px-4 py-1 text-sm font-medium text-slate-600 text-center">
            {rangeLabel}
          </span>
          <ToggleChips
            chips={RANGE_OPTIONS.map((option, index) => ({ key: option.label, value: index }))}
            selected={selectedRangeIndex}
            setSelected={setSelectedRangeIndex}
            variant="filled"
            width="6rem"
          />
        </div>
      </div>
      <div className="mt-4 flex flex-1 flex-col overflow-hidden">
        <div className="relative flex-1 min-h-[220px]">
          <Line
            data={chartData}
            options={chartOptions}
            redraw={false}
            className="absolute inset-0"
            style={{ height: '100%', width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
};
